import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import digitalTrackingImage from "../../../assets/images-webp/blog4digitalwaste.webp"; // Add your image path
import ScrollToTopButton from "../../ScrollToTopButton";
import SectionHeaderNoPDF from "../../SectionHeaderNoPDF";

const DigitalWasteTracking = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Tracking Waste Digitally"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "Blogs" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="Tracking Waste Digitally @ AJEEVI"
          shareUrl="https://swmecom.ajeevi.in/blog/tracking-waste-digitally"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Page content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Summary Section with Image */}
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="lg:w-2/3">
              <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                Summary
              </h4>
              <p className="text-black mb-4 text-lg">
                Digital technologies are revolutionizing waste management by enabling real-time tracking of waste generation, movement, and disposal through RFID, GPS, and cloud-based systems.
              </p>
              <p className="text-black text-lg">
                These tools enhance transparency across the waste lifecycle, helping municipalities and waste companies improve accountability, streamline logistics, and achieve sustainability goals with greater operational efficiency.
              </p>
            </div>
            <div className="lg:w-1/3">
              <img 
                src={digitalTrackingImage} 
                alt="Digital waste tracking dashboard" 
                className="w-full h-auto rounded-lg shadow-lg object-cover"
              />
              <p className="text-sm text-gray-500 mt-2 italic">
                Real-time digital tracking of waste collection operations
              </p>
            </div>
          </div>

          {/* Introduction Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-black mb-4 text-lg">
                  In the era of smart cities, waste management systems are undergoing a digital revolution, replacing paper-based methods with IoT sensors, RFID technology, and cloud computing for end-to-end visibility.
                </p>
                <p className="text-black text-lg">
                  These connected systems improve operational efficiency while enabling data-driven decisions for sustainable waste practices, from collection to processing and final disposal.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#00D084]">
                <h5 className="font-bold text-lg mb-2">Digital Tracking Impact</h5>
                <p className="text-gray-700">
                  Cities using digital tracking report 30-40% reductions in collection costs and 50% fewer missed pickups.
                </p>
              </div>
            </div>
          </div>

          {/* Key Technologies Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              Digital Tracking Technologies
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-blue-500">
                  <h6 className="font-semibold text-lg mb-2">RFID Tagging</h6>
                  <p className="text-gray-700">
                    Unique identification tags on bins enable precise tracking from source to disposal with scan rates up to 200 tags per second.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-green-500">
                  <h6 className="font-semibold text-lg mb-2">GPS Fleet Tracking</h6>
                  <p className="text-gray-700">
                    Real-time vehicle monitoring optimizes routes, reducing fuel use by 15-25% while improving service reliability.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-purple-500">
                  <h6 className="font-semibold text-lg mb-2">Cloud Platforms</h6>
                  <p className="text-gray-700">
                    Centralized dashboards aggregate data from multiple sources for comprehensive waste analytics and reporting.
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-red-500">
                  <h6 className="font-semibold text-lg mb-2">Smart Bin Sensors</h6>
                  <p className="text-gray-700">
                    Fill-level sensors enable data-driven collection, reducing overflow complaints by up to 80%.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-yellow-500">
                  <h6 className="font-semibold text-lg mb-2">Blockchain</h6>
                  <p className="text-gray-700">
                    Immutable ledgers track waste transactions and recycling credits, enhancing supply chain transparency.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-indigo-500">
                  <h6 className="font-semibold text-lg mb-2">Mobile Apps</h6>
                  <p className="text-gray-700">
                    Field staff and citizens report issues and track collections in real-time through intuitive interfaces.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Visual */}
          <div className="mb-10 bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-4 text-black text-center">
              Operational Benefits
            </h5>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">40%</div>
                <p className="text-gray-700">Reduction in collection costs</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">99%</div>
                <p className="text-gray-700">Tracking accuracy rate</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">50%</div>
                <p className="text-gray-700">Fewer service complaints</p>
              </div>
            </div>
          </div>

          {/* Conclusion Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">Conclusion</h5>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="text-black text-lg">
                  Digital tracking represents the future of efficient and sustainable waste management, enabling the transition from reactive to proactive operations. By reducing environmental impact and improving service quality, these technologies help build smarter, cleaner communities. When integrated with AI and advanced analytics, digital tracking systems gain predictive capabilities that further optimize waste management ecosystems.
                </p>
              </div>
              <div className="md:w-1/3 bg-[#00D084] bg-opacity-10 p-4 rounded-lg">
                <h6 className="font-semibold text-lg mb-2">Future Trends</h6>
                <p className="text-gray-700">
                  Emerging technologies like computer vision and digital twins will enhance tracking precision and simulation capabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Ajeevi's Contribution Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Ajeevi's Digital Tracking Solutions
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-black mb-4 text-lg">
                  Ajeevi Technologies offers comprehensive digital tracking platforms tailored for modern waste management needs, combining hardware, software, and analytics for complete operational visibility.
                </p>
                <p className="text-black text-lg">
                  Discover how Ajeevi can transform your waste operations at{" "}
                  <a
                    href="http://www.ajeevi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    www.ajeevi.com
                  </a>.
                </p>
              </div>
              <div>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    <strong>End-to-End Platform:</strong> Integrated tracking from bins to processing facilities
                  </li>
                  <li>
                    <strong>Custom RFID Solutions:</strong> Durable tags designed for waste environments
                  </li>
                  <li>
                    <strong>Real-Time Analytics:</strong> Customizable dashboards with actionable insights
                  </li>
                  <li>
                    <strong>Compliance Tools:</strong> Automated reporting for regulations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DigitalWasteTracking;